import { Injectable, inject } from '@angular/core';
import { of, Subject } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import { ProductV2 } from '../models/product-v2.model';
import { ProductV2Service } from '../services/product-v2.service';
import {
  clearStoredExternalChannelCampaign,
  readStoredExternalChannelCampaign,
} from '../utils/external-channel-access.util';

import { clearCampaignLoginFlowTypes, writeCampaignLoginFlowTypes } from './campaign-checkout-flag';
import { CampaignProductRef } from './campaign-contract';
import { CampaignReadService } from './campaign-read.service';

export interface CampaignScope {
  code: string;
  products: CampaignProductRef[];
  categoryUuids: string[];
}

@Injectable({ providedIn: 'root' })
export class CampaignScopeService {
  private readonly campaignRead = inject(CampaignReadService);
  private readonly productV2 = inject(ProductV2Service);
  private pendingCode = '';
  private pending: Promise<CampaignScope | null> | null = null;
  private requestId = 0;
  private readonly revisionSubject = new Subject<void>();
  readonly revision$ = this.revisionSubject.asObservable();

  load(): Promise<CampaignScope | null> {
    const code = this.codeFromUrlOrSession();
    if (!code) {
      clearCampaignLoginFlowTypes();
      return Promise.resolve(null);
    }

    if (this.pending && this.pendingCode === code) {
      return this.pending;
    }

    const requestId = ++this.requestId;
    this.pendingCode = code;
    this.pending = new Promise(resolve => {
      this.campaignRead
        .read(code)
        .pipe(
          take(1),
          switchMap(outcome => {
            if (outcome.kind === 'notFound') {
              clearStoredExternalChannelCampaign();
            }
            if (outcome.kind !== 'active') {
              clearCampaignLoginFlowTypes();
              return of(null);
            }
            writeCampaignLoginFlowTypes(outcome.campaign.flowTypes);
            return this.productV2.getProducts().pipe(
              take(1),
              map(response => this.toScope(outcome.campaign.code, outcome.campaign.products, response?.data ?? [])),
              catchError(() => of(this.toScope(outcome.campaign.code, outcome.campaign.products, [])))
            );
          }),
          catchError(() => of(null))
        )
        .subscribe(scope => {
          if (requestId !== this.requestId) {
            resolve(scope);
            return;
          }
          this.pending = null;
          resolve(scope);
        });
    });

    return this.pending;
  }

  invalidate(): void {
    this.requestId += 1;
    this.pending = null;
    this.pendingCode = '';
    this.revisionSubject.next();
  }

  private codeFromUrlOrSession(): string {
    if (typeof window === 'undefined') {
      return readStoredExternalChannelCampaign();
    }

    const query = (new URLSearchParams(window.location.search).get('campaign') ?? '').trim();
    return query || readStoredExternalChannelCampaign();
  }

  private toScope(
    code: string,
    products: CampaignProductRef[],
    catalog: ProductV2[]
  ): CampaignScope {
    const allowed = new Set(products.map(product => product.productUuid));
    const categoryUuids = new Set<string>();

    for (const product of catalog ?? []) {
      if (!allowed.has(product.uuid)) {
        continue;
      }
      for (const category of product.categories ?? []) {
        if (category?.uuid) {
          categoryUuids.add(category.uuid);
        }
      }
    }

    return { code, products, categoryUuids: [...categoryUuids] };
  }
}
